/**
 * FirmUserSelect
 *
 * Reusable async-paginated dropdown for selecting firm users.
 * Mirrors the LocationSelect pattern — wraps AsyncPaginate with the
 * loadFirmUserOptions loader and shared AsyncSelect styles.
 *
 * Multi-select behaviour:
 *  - Each option shows a checkbox to communicate multi-select affordance.
 *  - Selected items are shown as "N users selected" in the control so the
 *    height stays fixed regardless of how many users are picked.
 *  - The menu stays open while selecting (closeMenuOnSelect=false).
 *  - Selected options remain visible in the list (hideSelectedOptions=false)
 *    so users can deselect by unchecking.
 *
 * @param {Object}            props
 * @param {Object|Array|null} props.value         - Selected value(s)
 * @param {Function}          props.onChange       - Change handler
 * @param {string}            [props.placeholder]
 * @param {boolean}           [props.isClearable]
 * @param {boolean}           [props.isDisabled]
 * @param {boolean}           [props.isSearchable]
 * @param {boolean}           [props.isMulti]      - Enables multi-select mode
 * @param {string}            [props.className]
 * @param {number}            [props.pageSize]
 * @param {Object}            [props.styles]
 * @param {string}            [props.name]
 * @param {string}            [props.id]
 * @param {Function}          [props.onBlur]
 * @param {Function}          [props.onFocus]
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadFirmUserOptions } from 'hooks/queries/useFirmUsersDropdown';
import '../shared/AsyncSelect.scss';
import './FirmUserSelect.scss';

// ── Custom Option: renders a checkbox alongside the label ─────────────────────

function CheckboxOption({ innerRef, innerProps, isSelected, isFocused, children }) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
    >
      <span
        className={`firm-user-checkbox${isSelected ? ' firm-user-checkbox--checked' : ''}`}
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function FirmUserSelect({
  value = null,
  onChange,
  placeholder = 'Select firm user',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  isMulti = false,
  className = '',
  pageSize = 20,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) => loadFirmUserOptions(search, loadedOptions, additional, { pageSize }),
    [pageSize]
  );

  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: '38px',
        maxHeight: '38px',
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }),
      valueContainer: (base) => ({
        ...base,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
      }),
      multiValue: (base) => ({
        ...base,
        flexShrink: 0, // prevent chips from squishing when siblings are added
        minWidth: 'fit-content',
      }),
      option: (base) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        padding: '4px 8px',
      }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      ...styles,
    }),
    [styles]
  );

  const components = useMemo(() => ({ Option: CheckboxOption }), []);

  return (
    <AsyncPaginate
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      isMulti={isMulti}
      hideSelectedOptions={false}
      closeMenuOnSelect={!isMulti}
      className={`firm-user-select-dropdown ${className}`.trim()}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      components={components}
      additional={{ nextPage: '' }}
      debounceTimeout={300}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading users...'}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No users found for "${inputValue}"` : 'No users available')}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

FirmUserSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  className: PropTypes.string,
  pageSize: PropTypes.number,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default FirmUserSelect;
